import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addTemplate, updateTemplate } from '../../features/equipment/equipmentSlice';
import { generateId } from '../../utils/geometry';
import {
  CATEGORY_LABELS,
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_LAYER_VERSION,
  OBJECT_TYPES,
  OBJECT_TYPE_LABELS,
  defaultStyleForType,
} from '../../features/equipment/equipmentConstants';
import type {
  EquipmentCategory,
  EquipmentObjectType,
  EquipmentPort,
  EquipmentTemplate,
  PortType,
} from '../../features/equipment/equipmentTypes';

type PropRow = { id: string; key: string; value: string };

interface Props {
  /** Template id to edit, or null to create a new template. */
  templateId: string | null;
  open: boolean;
  onClose: () => void;
}

const PORT_TYPES: PortType[] = ['input', 'output', 'bidirectional'];

const EquipmentTemplateModal: React.FC<Props> = ({ templateId, open, onClose }) => {
  const dispatch = useAppDispatch();
  const existing = useAppSelector((s) => (templateId ? s.equipment.templates[templateId] : null));

  const [name, setName] = useState('');
  const [objectType, setObjectType] = useState<EquipmentObjectType>('equipment');
  const [category, setCategory] = useState<EquipmentCategory>('Photolithography');
  const [icon, setIcon] = useState('🛠');
  const [color, setColor] = useState('#2563eb');
  const [fillOpacity, setFillOpacity] = useState(0.14);
  const [ports, setPorts] = useState<EquipmentPort[]>([]);
  const [props, setProps] = useState<PropRow[]>([]);

  // Initialise the form (during render) each time the modal opens for a target.
  const [initSig, setInitSig] = useState<string | null>(null);
  const sig = open ? (templateId ?? 'new') : null;
  if (open && initSig !== sig) {
    setInitSig(sig);
    if (existing) {
      setName(existing.name);
      setObjectType(existing.objectType);
      setCategory(existing.category);
      setIcon(existing.defaultStyle.icon);
      setColor(existing.defaultStyle.color);
      setFillOpacity(existing.defaultStyle.fillOpacity);
      setPorts(JSON.parse(JSON.stringify(existing.ports)));
      setProps(
        Object.entries(existing.defaultProperties).map(([key, value]) => ({
          id: generateId(),
          key,
          value: String(value),
        }))
      );
    } else {
      const style = defaultStyleForType('equipment');
      setName('New Template');
      setObjectType('equipment');
      setCategory('Photolithography');
      setIcon(style.icon);
      setColor(style.color);
      setFillOpacity(style.fillOpacity);
      setPorts([]);
      setProps([]);
    }
  } else if (!open && initSig !== null) {
    setInitSig(null);
  }

  if (!open) return null;

  const addPort = () => {
    setPorts((prev) => [
      ...prev,
      {
        id: generateId(),
        name: `Port ${prev.length + 1}`,
        rx: 1,
        ry: 0.5,
        type: 'bidirectional',
        direction: 0,
      },
    ]);
  };

  const updatePortField = (id: string, changes: Partial<EquipmentPort>) => {
    setPorts((prev) => prev.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  };

  const removePort = (id: string) => setPorts((prev) => prev.filter((p) => p.id !== id));

  const addProp = () =>
    setProps((prev) => [...prev, { id: generateId(), key: '', value: '' }]);
  const updateProp = (id: string, changes: Partial<PropRow>) =>
    setProps((prev) => prev.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  const removeProp = (id: string) => setProps((prev) => prev.filter((p) => p.id !== id));

  const handleSave = () => {
    const defaultProperties: Record<string, unknown> = {};
    for (const row of props) {
      if (!row.key.trim()) continue;
      const num = Number(row.value);
      defaultProperties[row.key.trim()] =
        row.value.trim() !== '' && !Number.isNaN(num) ? num : row.value;
    }

    const iso = new Date().toISOString();
    if (existing) {
      dispatch(
        updateTemplate({
          id: existing.id,
          changes: {
            name: name.trim() || existing.name,
            objectType,
            category,
            icon,
            defaultStyle: { color, fillOpacity, icon },
            ports,
            defaultProperties,
          },
        })
      );
    } else {
      const template: EquipmentTemplate = {
        id: generateId(),
        name: name.trim() || 'New Template',
        objectType,
        category,
        icon,
        defaultStyle: { color, fillOpacity, icon },
        ports,
        defaultProperties,
        metadata: { version: EQUIPMENT_LAYER_VERSION },
        createdAt: iso,
        updatedAt: iso,
      };
      dispatch(addTemplate(template));
    }
    onClose();
  };

  const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';
  const inputClass =
    'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-4 border-b border-gray-200">
          <div className="text-lg font-semibold text-gray-900">
            {existing ? 'Edit Template' : 'Create Template'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Templates define reusable defaults (ports, properties, style) that instances inherit.
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
          {/* Identity */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelClass}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Icon</label>
              <input
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                className={inputClass}
                maxLength={4}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Object Type</label>
              <select
                value={objectType}
                onChange={(e) => setObjectType(e.target.value as EquipmentObjectType)}
                className={inputClass}
              >
                {OBJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {OBJECT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as EquipmentCategory)}
                className={inputClass}
              >
                {EQUIPMENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Style */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className={labelClass}>Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-300"
              />
            </div>
            <div>
              <label className={labelClass}>Fill Opacity ({fillOpacity.toFixed(2)})</label>
              <input
                type="range"
                min={0}
                max={0.6}
                step={0.02}
                value={fillOpacity}
                onChange={(e) => setFillOpacity(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          {/* Ports */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">
                Ports (graph nodes for routing)
              </label>
              <button
                type="button"
                onClick={addPort}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                + Add port
              </button>
            </div>
            {ports.length === 0 ? (
              <div className="text-xs text-gray-400 italic">
                No ports. Ports become connection nodes for AI-generated AMHS routing.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-1.5 text-[10px] font-semibold text-gray-400 px-1">
                  <div className="col-span-2">Name</div>
                  <div className="col-span-3">Display Name</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-2">rx</div>
                  <div className="col-span-2">ry</div>
                  <div className="col-span-1">dir°</div>
                </div>
                {ports.map((port) => (
                  <div key={port.id} className="grid grid-cols-12 gap-1.5 items-center">
                    <input
                      value={port.name}
                      onChange={(e) => updatePortField(port.id, { name: e.target.value })}
                      className="col-span-2 rounded border border-gray-300 px-1.5 py-1 text-xs"
                    />
                    <input
                      value={String(port.displayName ?? '')}
                      onChange={(e) =>
                        updatePortField(port.id, { displayName: e.target.value })
                      }
                      placeholder="Display name"
                      className="col-span-3 rounded border border-gray-300 px-1.5 py-1 text-xs"
                    />
                    <select
                      value={port.type}
                      onChange={(e) =>
                        updatePortField(port.id, { type: e.target.value as PortType })
                      }
                      className="col-span-2 rounded border border-gray-300 px-1 py-1 text-xs"
                    >
                      {PORT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={port.rx}
                      onChange={(e) => updatePortField(port.id, { rx: Number(e.target.value) })}
                      className="col-span-2 rounded border border-gray-300 px-1 py-1 text-xs"
                    />
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={port.ry}
                      onChange={(e) => updatePortField(port.id, { ry: Number(e.target.value) })}
                      className="col-span-2 rounded border border-gray-300 px-1 py-1 text-xs"
                    />
                    <div className="col-span-1 flex items-center gap-1">
                      <input
                        type="number"
                        value={port.direction}
                        onChange={(e) =>
                          updatePortField(port.id, { direction: Number(e.target.value) })
                        }
                        className="w-full rounded border border-gray-300 px-1 py-1 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => removePort(port.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                        title="Remove port"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Default properties */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">Default properties</label>
              <button
                type="button"
                onClick={addProp}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                + Add property
              </button>
            </div>
            {props.length === 0 ? (
              <div className="text-xs text-gray-400 italic">
                No properties (e.g. throughput, vendor, model).
              </div>
            ) : (
              <div className="space-y-2">
                {props.map((row) => (
                  <div key={row.id} className="grid grid-cols-12 gap-1.5 items-center">
                    <input
                      placeholder="key"
                      value={row.key}
                      onChange={(e) => updateProp(row.id, { key: e.target.value })}
                      className="col-span-5 rounded border border-gray-300 px-1.5 py-1 text-xs"
                    />
                    <input
                      placeholder="value"
                      value={row.value}
                      onChange={(e) => updateProp(row.id, { value: e.target.value })}
                      className="col-span-6 rounded border border-gray-300 px-1.5 py-1 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => removeProp(row.id)}
                      className="col-span-1 text-red-500 hover:text-red-700 text-sm"
                      title="Remove property"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-gray-300 text-sm text-gray-700 hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
          >
            {existing ? 'Save Changes' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentTemplateModal;
